'use client'

import Link from 'next/link'

const FOLLOWER_THRESHOLD = 10

interface AuthRequiredProps {
  title?: string
  listingName?: string
  listingType?: 'market' | 'producer' | 'member'
  tab?: string
  followerCount?: number
  totalCount?: number
}

function buildMessage(
  listingName: string | undefined,
  listingType: AuthRequiredProps['listingType'],
  tab: string | undefined,
  totalCount: number | undefined,
): { headline: string; sub: string } {
  const name = listingName || 'this listing'
  const countLabel = totalCount ? `all ${totalCount} ` : ''

  if (listingType === 'market') {
    if (tab === 'vendors') {
      return {
        headline: `See ${countLabel}vendors at ${name} — free.`,
        sub: "Members see the full vendor lineup and what's in season right now.",
      }
    }
    if (tab === 'recipes') {
      return {
        headline: `See recipes from ${name} — free.`,
        sub: 'Members see market recipes, seasonal picks, and what to cook this week.',
      }
    }
    if (tab === 'feed') {
      return {
        headline: `Stay up to date with ${name} — free.`,
        sub: 'Members see market announcements, vendor spotlights, and seasonal news.',
      }
    }
    if (tab === 'locations') {
      return {
        headline: `See where to find ${name} — free.`,
        sub: 'Members see all market locations, days, and hours.',
      }
    }
    return {
      headline: `Follow ${name} to stay in the loop — free.`,
      sub: 'Get notified about market days, vendors, and seasonal highlights.',
    }
  }

  if (listingType === 'producer') {
    if (tab === 'products') {
      return {
        headline: `See what ${name} is growing — free.`,
        sub: 'Members see full product listings, seasonal availability, and where to buy.',
      }
    }
    if (tab === 'recipes') {
      return {
        headline: `See recipes from ${name} — free.`,
        sub: 'Members see the full recipe collection and can save their favorites.',
      }
    }
    if (tab === 'feed') {
      return {
        headline: `Follow what ${name} is up to — free.`,
        sub: 'Members see farm updates, seasonal announcements, and market appearances.',
      }
    }
    if (tab === 'locations') {
      return {
        headline: `Find ${name} near you — free.`,
        sub: 'Members see all farm stand locations, markets, and pickup details.',
      }
    }
    return {
      headline: `Follow ${name} for seasonal updates — free.`,
      sub: 'Know exactly when new products and market appearances are posted.',
    }
  }

  if (listingType === 'member') {
    return {
      headline: `${name} is part of your local food community.`,
      sub: 'Join free to connect, follow, and share recipes together.',
    }
  }

  return {
    headline: 'Join the conversation.',
    sub: 'Create a free account to comment and connect with local food lovers.',
  }
}

export const AuthRequired = ({
  title,
  listingName,
  listingType,
  tab,
  followerCount,
  totalCount,
}: AuthRequiredProps) => {
  const useContextual = listingName || listingType || tab

  const { headline, sub } = useContextual
    ? buildMessage(listingName, listingType, tab, totalCount)
    : { headline: title || 'Join to see more.', sub: '' }

  const showSocialProof =
    listingName &&
    followerCount !== undefined &&
    followerCount >= FOLLOWER_THRESHOLD

  return (
    <div className="mt-6 rounded-2xl border bg-gray-50 p-6 text-center shadow-sm">
      <div className="text-5xl mb-4 mt-2">🌿</div>
      <p className="mb-1 text-lg font-semibold text-gray-900">{headline}</p>
      {sub && (
        <p className="mb-4 text-sm text-gray-500">{sub}</p>
      )}
      {showSocialProof && (
        <p className="mb-4 text-sm font-medium text-[#85C349]">
          Join {followerCount} others following {listingName}
        </p>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Link
          href="/auth/join-culineer"
          className="rounded-full bg-[#85C349] px-8 py-3 font-semibold text-white transition-all hover:bg-[#75B339] hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#85C349] focus:ring-offset-2 active:scale-95"
        >
          Join free
        </Link>
        <Link
          href="/auth/login"
          className="rounded-full border-2 border-[#85C349] bg-white px-8 py-3 font-semibold text-[#85C349] transition-all hover:bg-[#F3FAEB] hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#85C349] focus:ring-offset-2 active:scale-95"
        >
          Sign in
        </Link>
      </div>
    </div>
  )
}
